import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-[var(--accent-border)] bg-[var(--accent-bg-outline)] text-[var(--accent-text-on-outline)] hover:bg-[var(--accent-bg-outline)]/80",
        outline:
          "text-foreground border-border hover:bg-accent hover:text-accent-foreground",
        success:
          "border-[var(--accent-border)] bg-[var(--accent-bg-outline)] text-[var(--accent-text-on-outline)] hover:bg-[var(--accent-bg-outline)]/80",
        warning:
          "border-[var(--accent-border)] bg-[var(--accent-bg-outline)] text-[var(--accent-text-on-outline)] hover:bg-[var(--accent-bg-outline)]/80",
        info: "border-[var(--accent-border)] bg-[var(--accent-bg-outline)] text-[var(--accent-text-on-outline)] hover:bg-[var(--accent-bg-outline)]/80",
        accent:
          "border-[var(--accent-border)] bg-[var(--accent-bg-outline)] text-[var(--accent-text-on-outline)] hover:bg-[var(--accent-bg-outline)]/80",
        solid:
          "border-[var(--accent-border)] bg-[var(--accent-bg-solid)] text-[var(--accent-text-on-solid)] hover:bg-[var(--accent-bg-solid)]/80",
        // Maximus Table extended colour variants
        purple: "border-transparent bg-purple-500 text-white",
        sky: "border-transparent bg-sky-500 text-white",
        cyan: "border-transparent bg-cyan-500 text-white",
        indigo: "border-transparent bg-indigo-500 text-white",
        emerald: "border-transparent bg-emerald-500 text-white",
        sapgreen: "border-transparent bg-green-700 text-white",
        burntorange: "border-transparent bg-orange-600 text-white",
        brickred: "border-transparent bg-red-800 text-white",
        accessred: "border-transparent bg-red-600 text-white",
        electricviolet: "border-transparent bg-violet-500 text-white",
        blueviolet: "border-transparent bg-blue-700 text-white",
        // Inverted variants — light bg, dark text
        successInverted: "border-transparent bg-green-100 text-green-700",
        warningInverted: "border-transparent bg-yellow-100 text-yellow-700",
        infoInverted: "border-transparent bg-blue-100 text-blue-700",
        purpleInverted: "border-transparent bg-purple-100 text-purple-700",
        skyInverted: "border-transparent bg-sky-100 text-sky-700",
        cyanInverted: "border-transparent bg-cyan-100 text-cyan-700",
        indigoInverted: "border-transparent bg-indigo-100 text-indigo-700",
        emeraldInverted: "border-transparent bg-emerald-100 text-emerald-700",
        sapgreenInverted: "border-transparent bg-green-100 text-green-700",
        burntorangeInverted: "border-transparent bg-orange-100 text-orange-700",
        brickredInverted: "border-transparent bg-red-100 text-red-800",
        accessredInverted: "border-transparent bg-red-100 text-red-600",
        electricvioletInverted:
          "border-transparent bg-violet-100 text-violet-700",
        bluevioletInverted: "border-transparent bg-blue-100 text-blue-700",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  accent?:
    | "teal"
    | "purple"
    | "info"
    | "success"
    | "warning"
    | "destructive"
    | "nile"
    | "cerulean"
    | "lagoon"
    | "eagle"
    | "sap"
    | "orange"
    | "brick"
    | "red"
    | "violet"
    | "blue-violet";
  onClose?: () => void;
}

function Badge({
  className,
  variant,
  accent,
  onClose,
  children,
  ...props
}: BadgeProps) {
  // Always apply the correct accent class for status variants if accent is not provided
  let accentClass = "";
  if (accent) {
    accentClass = `accent-${accent}`;
  } else if (
    variant === "success" ||
    variant === "warning" ||
    variant === "info" ||
    variant === "destructive"
  ) {
    accentClass = `accent-${variant}`;
  }

  return (
    <div
      data-slot="badge"
      className={cn(
        badgeVariants({ variant }),
        accentClass,
        onClose && "pr-1.5",
        className,
      )}
      {...props}
    >
      {children}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="ml-1 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10 transition-colors cursor-pointer"
          aria-label="Remove badge"
        >
          <X size={10} />
        </button>
      )}
    </div>
  );
}

export { Badge, badgeVariants };
